import { createIcon as baseCreateIcon } from '@chakra-ui/react';
import {
  Children,
  forwardRef,
  type ForwardRefExoticComponent,
  type RefAttributes,
} from 'react';

import { Icon, type IconProps } from './Icon';

export type CreateIconOptions = Parameters<typeof baseCreateIcon>[0];
export type CreateIconType = React.ForwardRefExoticComponent<
  IconProps & {
    size?: string | number | undefined;
  } & React.RefAttributes<SVGSVGElement>
>;

/**
 * Helper to create new icon components from SVG.
 *
 * This is forked from Chakra's own createIcon helper, the only difference being
 * that it uses our Icon component, which allows `boxSize` or `size`.
 *
 * @see https://github.com/chakra-ui/chakra-ui/blob/main/packages/react/src/components/icon/create-icon.tsx
 */
export const createIcon = (
  options: CreateIconOptions,
): ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>> => {
  const {
    viewBox = '0 0 24 24',
    d: pathDefinition,
    displayName,
    defaultProps = {},
  } = options;
  const path = Children.toArray(options.path);

  // Parse viewBox to get aspect ratio
  const [, , viewBoxWidth, viewBoxHeight] = viewBox.split(' ').map(Number);

  const Comp = forwardRef<SVGSVGElement, IconProps>((props, forwardedRef) => {
    return (
      <Icon
        ref={forwardedRef}
        viewBox={viewBox}
        aspectRatio={viewBoxWidth / viewBoxHeight}
        {...defaultProps}
        {...props}
      >
        {path.length ? path : <path fill="currentColor" d={pathDefinition} />}
      </Icon>
    );
  });
  Comp.displayName = displayName;

  return Comp;
};
